"use client";

import { useEffect, useMemo, useState } from "react";
import { FaFacebook, FaInstagram, FaTiktok } from "react-icons/fa";
import useSecretTapTrigger from "../hooks/useSecretTapTrigger";
import { useLanguage } from "../contexts/LanguageContext";
import { getLandingText } from "../locales/landing";
import { siteContact, phoneToTelHref } from "../config/siteContact";
import { useSystemSettings } from "../contexts/SystemSettingsContext";
import { hasVisibleSocialLinks, hasVisibleStoreBadges, resolveLandingLinks } from "../utils/landingLinks";
import { isMobileWebShortcutEnabled, openAdminQuickLogin } from "../utils/adminSecretAccess";
import LibertyBrandLogo from "./landing/LibertyBrandLogo";

function StoreBadge({ t, type, link, badgeClass }) {
  const isAppStore = type === "appStore";
  const label = isAppStore ? t.store_app_store : t.store_google_play;
  const comingSoon = !link.url;

  const content = (
    <>
      {isAppStore ? (
        <svg viewBox="0 0 24 24" className="h-8 w-8 shrink-0 fill-white" aria-hidden="true">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="h-8 w-8 shrink-0 fill-white" aria-hidden="true">
          <path d="M3.609 1.814 13.792 12 3.61 22.186a1.4 1.4 0 0 1-.247-.236 1.4 1.4 0 0 1-.233-.555V2.05c0-.212.08-.416.233-.555.053-.09.14-.177.236-.247M14.5 13.5l2.95 2.95-10.2 5.92a1.4 1.4 0 0 1-.18-.104L14.5 13.5m1.04-3.04 3.18 1.84a1.4 1.4 0 0 1 0 2.42l-3.18 1.84L13.08 12l2.46-1.54M14.5 10.5 5.87 4.724a1.4 1.4 0 0 1 .18-.104l10.2 5.92L14.5 10.5Z" />
        </svg>
      )}
      <div className="text-start leading-tight">
        {comingSoon ? (
          <div className="text-[11px] font-semibold uppercase tracking-wide text-[#ff8c5a]">
            {t.store_coming_soon}
          </div>
        ) : null}
        <div className="text-base font-bold text-white">{label}</div>
      </div>
    </>
  );

  if (link.url) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${badgeClass} cursor-pointer hover:scale-[1.03]`}
        aria-label={label}
      >
        {content}
      </a>
    );
  }

  return (
    <div className={`${badgeClass} cursor-default`} aria-label={label}>
      {content}
    </div>
  );
}

function StoreBadges({ t, links, className = "" }) {
  const badgeClass =
    "inline-flex min-w-[210px] items-center gap-3 rounded-xl border-2 border-[#ff6b35]/30 bg-[#1a0d2e] px-5 py-3.5 text-white shadow-lg shadow-black/40 transition hover:border-[#ff6b35] hover:shadow-xl hover:shadow-[#ff6b35]/15";

  const items = [];
  if (links.appStore.enabled) items.push({ type: "appStore", link: links.appStore });
  if (links.googlePlay.enabled) items.push({ type: "googlePlay", link: links.googlePlay });

  if (!items.length) return null;

  return (
    <div className={`flex flex-wrap gap-4 ${className}`}>
      {items.map(({ type, link }) => (
        <StoreBadge key={type} t={t} type={type} link={link} badgeClass={badgeClass} />
      ))}
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-[#ff6b35]/20 bg-white/5 p-8 shadow-lg backdrop-blur-sm transition duration-500 hover:-translate-y-2 hover:scale-[1.02] hover:border-[#ff6b35]/45 hover:shadow-2xl hover:shadow-[#ff6b35]/10">
      <div className="absolute inset-0 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-[#ff6b35]/8 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
      <div className="relative mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-[#ff6b35] to-[#ff8c5a] text-2xl shadow-lg shadow-[#ff6b35]/30 transition group-hover:scale-110">
        {icon}
      </div>
      <h3 className="relative mb-4 text-center text-xl font-bold text-white transition group-hover:text-[#ff8c5a]">
        {title}
      </h3>
      <p className="relative text-center leading-relaxed text-gray-300 transition group-hover:text-gray-200">
        {desc}
      </p>
    </div>
  );
}

function Home() {
  const { language, changeLanguage, dir } = useLanguage();
  const { settings: publicSettings } = useSystemSettings();
  const t = getLandingText(language);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [mobileAdminShortcut, setMobileAdminShortcut] = useState(false);
  const onLogoSecretTap = useSecretTapTrigger(openAdminQuickLogin);

  const particles = useMemo(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        left: `${(i * 17 + 7) % 100}%`,
        top: `${(i * 23 + 11) % 100}%`,
        delay: `${(i % 5) * 0.6}s`,
        duration: `${2 + (i % 4)}s`,
      })),
    []
  );

  useEffect(() => {
    const sync = () => setMobileAdminShortcut(isMobileWebShortcutEnabled());
    sync();
    const mqNarrow = window.matchMedia("(max-width: 768px)");
    const mqCoarse = window.matchMedia("(pointer: coarse)");
    mqNarrow.addEventListener("change", sync);
    mqCoarse.addEventListener("change", sync);
    return () => {
      mqNarrow.removeEventListener("change", sync);
      mqCoarse.removeEventListener("change", sync);
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const setLang = (lang) => {
    if (lang === "fr" || lang === "ar") changeLanguage(lang);
  };

  const features = [
    { icon: "🔍", title: t.feature_find_title, desc: t.feature_find_desc },
    { icon: "💬", title: t.feature_price_title, desc: t.feature_price_desc },
    { icon: "📍", title: t.feature_track_title, desc: t.feature_track_desc },
    { icon: "📞", title: t.feature_chat_title, desc: t.feature_chat_desc },
    { icon: "⚡", title: t.feature_quick_title, desc: t.feature_quick_desc },
    { icon: "🌍", title: t.feature_intl_title, desc: t.feature_intl_desc },
  ];

  const steps = [
    { n: "1", title: t.how_step1_title, desc: t.how_step1_desc },
    { n: "2", title: t.how_step2_title, desc: t.how_step2_desc },
    { n: "3", title: t.how_step3_title, desc: t.how_step3_desc },
  ];

  const siteName = (publicSettings?.siteName || "Liberty").trim() || "Liberty";
  const contactEmail = (publicSettings?.contactEmail || siteContact.email).trim() || siteContact.email;
  const contactPhone = (publicSettings?.contactPhone || siteContact.phone).trim() || siteContact.phone;
  const contactPhoneHref = phoneToTelHref(contactPhone) || siteContact.phoneHref;
  const landingLinks = resolveLandingLinks(publicSettings);
  const showStoreBadges = hasVisibleStoreBadges(landingLinks);
  const showSocialLinks = hasVisibleSocialLinks(landingLinks);

  const socialItems = [
    { key: "facebook", Icon: FaFacebook, link: landingLinks.facebook, label: "Facebook" },
    { key: "instagram", Icon: FaInstagram, link: landingLinks.instagram, label: "Instagram" },
    { key: "tiktok", Icon: FaTiktok, link: landingLinks.tiktok, label: "TikTok" },
  ].filter((item) => item.link.enabled && item.link.url);

  const navLinkClass =
    "rounded-md px-3 py-2 text-sm font-medium text-gray-300 transition hover:scale-105 hover:bg-[#ff6b35]/10 hover:text-[#ff8c5a]";

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#0f0c29] text-white" dir={dir}>
      <nav
        className={`fixed z-50 w-full border-b border-[#ff6b35]/25 transition-all duration-300 ${
          scrollY > 50
            ? "bg-[#1a0d2e]/95 shadow-2xl shadow-[#ff6b35]/10 backdrop-blur-md"
            : "bg-[#1a0d2e]/90 shadow-xl shadow-[#ff6b35]/5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div
            role={mobileAdminShortcut ? "button" : undefined}
            tabIndex={mobileAdminShortcut ? -1 : undefined}
            onClick={mobileAdminShortcut ? onLogoSecretTap : undefined}
            className="cursor-pointer select-none transition hover:scale-105 touch-manipulation"
          >
            <LibertyBrandLogo siteName={siteName} size={38} />
          </div>

          <div className="hidden items-center gap-1 md:flex">
            <a href="#features" className={navLinkClass}>
              {t.nav_features}
            </a>
            <a href="#how" className={navLinkClass}>
              {t.nav_how}
            </a>
            <a href="#contact" className={navLinkClass}>
              {t.nav_contact}
            </a>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex rounded-md border border-[#ff6b35]/25 bg-[#261838] p-0.5 text-xs font-medium">
              <button
                type="button"
                onClick={() => setLang("fr")}
                className={`rounded px-2.5 py-1 transition ${
                  language !== "ar"
                    ? "bg-gradient-to-r from-[#ff6b35] to-[#ff8c5a] text-white"
                    : "text-gray-300 hover:text-[#ff8c5a]"
                }`}
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("ar")}
                className={`rounded px-2.5 py-1 transition ${
                  language === "ar"
                    ? "bg-gradient-to-r from-[#ff6b35] to-[#ff8c5a] text-white"
                    : "text-gray-300 hover:text-[#ff8c5a]"
                }`}
              >
                AR
              </button>
            </div>
            <a
              href="#download"
              className="hidden rounded-md bg-gradient-to-r from-[#ff6b35] to-[#ff8c5a] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#ff6b35]/25 transition hover:-translate-y-0.5 hover:from-[#ff8c5a] hover:to-[#ff6b35] sm:inline-block"
            >
              {t.nav_download}
            </a>
          </div>
        </div>
      </nav>

      <header className="relative flex min-h-screen items-center overflow-hidden bg-gradient-to-br from-[#1a0d2e] via-[#261838] to-[#0f0c29] pt-20">
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((p) => (
            <div
              key={p.id}
              className="absolute h-2 w-2 animate-pulse rounded-full bg-[#ff6b35]/25"
              style={{ left: p.left, top: p.top, animationDelay: p.delay, animationDuration: p.duration }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b35]/8 to-transparent" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-32">
          <div
            className={`text-center transition-all duration-1000 lg:text-start ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <span className="mb-4 inline-block rounded-full border border-[#ff6b35]/30 bg-[#1a0d2e]/80 px-3 py-1 text-xs font-medium uppercase tracking-wider text-[#ff8c5a]">
              {t.hero_badge}
            </span>
            <h2 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              {t.hero_title_lead}
              <span className="bg-gradient-to-r from-[#ff6b35] to-[#ff8c5a] bg-clip-text text-transparent">
                {t.hero_title_highlight}
              </span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl text-xl text-gray-300 lg:mx-0">{t.hero_subtitle}</p>
            <div className="mt-8">
              {showStoreBadges ? (
                <>
                  <p className="mb-3 text-sm font-medium text-gray-400">{t.hero_cta}</p>
                  <StoreBadges t={t} links={landingLinks} className="justify-center lg:justify-start" />
                </>
              ) : null}
            </div>
          </div>

          <div
            className={`flex justify-center transition-all delay-300 duration-1000 lg:justify-end ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="relative w-64 sm:w-72">
              <div className="rounded-[2.5rem] border-4 border-[#ff6b35]/35 bg-[#1a0d2e] p-3 shadow-2xl shadow-[#ff6b35]/15">
                <div className="overflow-hidden rounded-[2rem] bg-black">
                  <img
                    src={`${process.env.PUBLIC_URL}/liberty-app-screenshot.jpeg`}
                    alt="Liberty — capture d'écran de l'application"
                    className="block w-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="absolute -end-4 -top-4 animate-pulse rounded-full bg-gradient-to-r from-[#ff6b35] to-[#ff8c5a] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#ff6b35]/30">
                ⭐ 4.9/5
              </div>
              <div className="absolute -start-8 -top-8 h-16 w-16 animate-ping rounded-full bg-[#ff6b35]/20" />
              <div
                className="absolute -bottom-8 -end-8 h-12 w-12 animate-ping rounded-full bg-[#ff6b35]/25"
                style={{ animationDelay: "1s" }}
              />
              <div className="absolute -bottom-4 -start-4 rounded-full border border-[#ff6b35]/30 bg-[#1a0d2e] px-4 py-2 text-sm font-semibold text-[#ff8c5a] shadow-lg">
                🇹🇳 Tunisia
              </div>
            </div>
          </div>
        </div>
      </header>

      <section id="features" className="relative bg-[#1a0d2e] py-20">
        <div className="absolute start-0 top-0 h-32 w-32 rounded-full bg-gradient-to-br from-[#ff6b35]/15 to-transparent blur-3xl" />
        <div className="absolute bottom-0 end-0 h-40 w-40 rounded-full bg-gradient-to-tl from-[#ff6b35]/15 to-transparent blur-3xl" />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="text-base font-semibold uppercase tracking-wide text-[#ff8c5a]">
              {t.nav_features}
            </h2>
            <p className="mt-2 text-4xl font-bold text-white">{t.section_app_title}</p>
            <p className="mx-auto mt-4 max-w-3xl text-xl text-gray-300">{t.section_app_subtitle}</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <FeatureCard key={f.title} {...f} />
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-[#0f0c29] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="rounded-2xl border border-[#ff6b35]/30 bg-[#1a0d2e]/90 p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-bold text-[#ff8c5a]">{t.audience_clients_title}</h3>
              <p className="mt-3 text-gray-300">{t.audience_clients_desc}</p>
              <ul className="mt-6 space-y-3">
                {[t.audience_clients_1, t.audience_clients_2, t.audience_clients_3].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-gray-200">
                    <span className="text-[#ff6b35]">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
              <h3 className="text-2xl font-bold text-white">{t.audience_movers_title}</h3>
              <p className="mt-3 text-gray-300">{t.audience_movers_desc}</p>
              <ul className="mt-6 space-y-3">
                {[t.audience_movers_1, t.audience_movers_2, t.audience_movers_3].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-gray-200">
                    <span className="text-[#ff6b35]">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="relative bg-[#1a0d2e] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 className="text-base font-semibold uppercase tracking-wide text-[#ff8c5a]">{t.how_title}</h2>
            <p className="mt-2 text-4xl font-bold text-white">{t.how_subtitle}</p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {steps.map((step) => (
              <div key={step.n} className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#ff6b35] to-[#ff8c5a] text-xl font-bold text-white shadow-lg shadow-[#ff6b35]/30">
                  {step.n}
                </div>
                <h3 className="text-lg font-bold text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-gray-300">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-r from-[#1a0d2e] via-[#261838] to-[#1a0d2e] py-16">
        <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b35]/8 to-transparent" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="text-2xl font-bold sm:text-3xl">{t.trust_title}</h2>
          <p className="mt-4 text-gray-300">{t.trust_desc}</p>
        </div>
      </section>

      <section
        id="download"
        className="relative overflow-hidden bg-gradient-to-r from-[#ff6b35] to-[#ff8c5a] py-20 text-white"
      >
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="text-3xl font-bold sm:text-4xl">{t.download_title}</h2>
          <p className="mt-4 text-lg text-white/90">{t.download_subtitle}</p>
          {showStoreBadges ? (
            <StoreBadges t={t} links={landingLinks} className="mt-8 justify-center" />
          ) : null}
        </div>
      </section>

      <footer id="contact" className="border-t border-[#ff6b35]/15 bg-[#0f0c29] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 md:grid-cols-3">
            <div>
              <LibertyBrandLogo siteName={siteName} size={36} textClassName="text-xl font-bold" />
              <p className="mt-3 text-sm leading-relaxed text-gray-400">{t.footer_tagline}</p>
              <a
                href="/privacy"
                className="mt-4 inline-block text-sm text-gray-400 transition hover:text-[#ff8c5a]"
              >
                Politique de confidentialité
              </a>
            </div>

            <div>
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">
                {t.footer_contact}
              </h4>
              <ul className="space-y-3 text-sm">
                {contactPhone ? (
                  <li>
                    <a href={contactPhoneHref} className="text-gray-300 transition hover:text-[#ff8c5a]">
                      📞 {contactPhone}
                    </a>
                  </li>
                ) : null}
                {contactEmail ? (
                  <li>
                    <a
                      href={`mailto:${contactEmail}`}
                      className="text-gray-300 transition hover:text-[#ff8c5a]"
                    >
                      ✉️ {contactEmail}
                    </a>
                  </li>
                ) : null}
              </ul>
            </div>

            <div>
              <h4 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-400">
                {t.footer_follow}
              </h4>
              {showSocialLinks ? (
                <div className="flex gap-4">
                  {socialItems.map(({ key, Icon, link, label }) => (
                    <a
                      key={key}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-[#ff6b35]/25 bg-[#1a0d2e] text-lg transition hover:border-[#ff6b35]/60 hover:bg-[#ff6b35]/10 hover:text-[#ff8c5a]"
                    >
                      <Icon />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">—</p>
              )}
            </div>
          </div>

          <div className="mt-12 border-t border-[#ff6b35]/15 pt-8 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} {siteName}. {t.footer_rights}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
